import type { TagItem } from '@/components/atoms/Tag/Tag.types';
import type { SelectOption } from '@/components/organisms/Select/types';
import type {
  CustomFilterApiKey,
  NewFilterGroupKey,
  NewFiltersOptions,
  NewMultiFilterKey,
  NewVisibleFilterKey,
} from '@/context/filters.context';

type FilterOptionKey = keyof NewFiltersOptions;

type BaseNewFilterFieldConfig = {
  icon: React.ReactNode;
  key: NewVisibleFilterKey;
  labelKey: string;
  optionKey: FilterOptionKey;

  disabled?: boolean;
};

export type CheckboxFilterFieldConfig = BaseNewFilterFieldConfig & {
  control: 'checkbox';
  direction: 'vertical';
  key: NewMultiFilterKey;
  name: string;
};

export type MultiSelectFilterFieldConfig = BaseNewFilterFieldConfig & {
  control: 'select';
  id: string;
  key: NewMultiFilterKey;
  mode: 'multiple' | 'tree-multiple';
  placeholderKey: string;

  maxTagCount?: number;
  searchable?: boolean;
};

export type SingleSelectFilterFieldConfig = BaseNewFilterFieldConfig & {
  control: 'select';
  id: string;
  key: 'hazardous';
  mode: 'single';
  placeholderKey: string;

  searchable?: boolean;
};

export type CustomFilterFieldConfig = {
  control: 'custom-filter';
  icon: React.ReactNode;
  id: string;
  key: CustomFilterApiKey;
  label: string;
  options: SelectOption[];
  placeholder: string;

  disabled?: boolean;
  maxTagCount?: number;
  searchable?: boolean;
};

export type NewFilterFieldConfig =
  CheckboxFilterFieldConfig | MultiSelectFilterFieldConfig | SingleSelectFilterFieldConfig | CustomFilterFieldConfig;

export type FilterFieldConfig = NewFilterFieldConfig;

export type FilterGroupConfig = {
  fields: readonly FilterFieldConfig[];
  key: NewFilterGroupKey;
  titleKey: string;
  tooltipKey: string;

  source?: 'customFilters';
};

export type ActiveFilterViewModel = {
  count: number;
  icon: React.ReactNode;
  items: TagItem[];
  key: NewVisibleFilterKey;
  label: string;

  clearable?: boolean;
  disabled?: boolean;
};

export function getFilterFields(filterGroups: readonly FilterGroupConfig[]): readonly FilterFieldConfig[] {
  return filterGroups.flatMap((group) => group.fields);
}

export function isSingleFilterField(field: FilterFieldConfig): field is SingleSelectFilterFieldConfig {
  return field.control === 'select' && field.key === 'hazardous';
}

export function isTreeFilterField(field: FilterFieldConfig): field is MultiSelectFilterFieldConfig {
  return field.control === 'select' && field.mode === 'tree-multiple';
}

export function isCustomFilterField(field: FilterFieldConfig): field is CustomFilterFieldConfig {
  return field.control === 'custom-filter';
}

export function isMultiFilterField(
  field: FilterFieldConfig
): field is CheckboxFilterFieldConfig | MultiSelectFilterFieldConfig | CustomFilterFieldConfig {
  return !isSingleFilterField(field);
}
